import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  LEAD_TAGS_QUERY_KEY,
  LeadTag,
  LeadTagChangeResponse,
  leadTagsApi,
  sortLeadTags
} from "api/lead-tags";
import { getErrorMessage } from "utils/error";
import {
  markTagDependentListsStale,
  updateLeadTagsInCaches
} from "shared/app/LeadTags/utils/leadTagCaches";

type LeadTagChange =
  | { kind: "add"; tag: LeadTag }
  | { kind: "create"; name: string }
  | { kind: "remove"; tag: LeadTag };

const withTag = (tags: LeadTag[], tag: LeadTag) =>
  sortLeadTags([...tags.filter((item) => item.id !== tag.id), tag]);

const withoutTag = (tags: LeadTag[], tag: LeadTag) =>
  tags.filter((item) => item.id !== tag.id);

// Changes to one Lead are sent one at a time, in click order (TanStack Query 5.8 has
// no mutation scope), so the last response to settle is the Lead's latest state.
const leadQueues = new Map<number, Promise<unknown>>();

const enqueueForLead = <T>(
  leadId: number,
  task: () => Promise<T>
): Promise<T> => {
  const previous = leadQueues.get(leadId) ?? Promise.resolve();
  const next = previous.then(task, task);
  leadQueues.set(leadId, next);
  const release = () => {
    if (leadQueues.get(leadId) === next) leadQueues.delete(leadId);
  };
  next.then(release, release);
  return next;
};

const callApi = (leadId: number, change: LeadTagChange) => {
  if (change.kind === "remove")
    return leadTagsApi.removeFromLead({ leadId, tagId: change.tag.id });
  if (change.kind === "add")
    return leadTagsApi.addToLead({ leadId, tagId: change.tag.id });
  return leadTagsApi.addToLead({ leadId, name: change.name });
};

/**
 * Adds or removes one tag on a Lead and saves immediately. Existing tags update the
 * caches optimistically; a new tag (admins) waits for its id. Each change is its own
 * request, so two quick clicks never overwrite each other.
 */
export const useLeadTagChange = ({ leadId }: { leadId: number }) => {
  const queryClient = useQueryClient();
  const mutationKey = [LEAD_TAGS_QUERY_KEY, "LEAD", leadId, "UPDATE"];

  return useMutation<LeadTagChangeResponse, Error, LeadTagChange>({
    mutationKey,
    mutationFn: (change) =>
      enqueueForLead(leadId, () => callApi(leadId, change)),
    onMutate: (change) => {
      if (change.kind === "add")
        updateLeadTagsInCaches(queryClient, leadId, (tags) =>
          withTag(tags, change.tag)
        );
      if (change.kind === "remove")
        updateLeadTagsInCaches(queryClient, leadId, (tags) =>
          withoutTag(tags, change.tag)
        );
    },
    onSuccess: (data, change) => {
      // Another click on this Lead may still be in flight; its response carries the
      // final list, so only the last settling change writes the server's list.
      if (queryClient.isMutating({ mutationKey }) <= 1)
        updateLeadTagsInCaches(queryClient, leadId, () => data.tags);
      else if (change.kind === "create")
        updateLeadTagsInCaches(queryClient, leadId, (tags) =>
          withTag(tags, data.tag)
        );

      markTagDependentListsStale(queryClient);
      // The master list only changes when a tag was created; counts change every time.
      if (change.kind === "create")
        queryClient.invalidateQueries({ queryKey: [LEAD_TAGS_QUERY_KEY] });
      else
        queryClient.invalidateQueries({
          queryKey: [LEAD_TAGS_QUERY_KEY, "WITH_COUNTS"],
          refetchType: "none"
        });
      queryClient.invalidateQueries({ queryKey: ["LEAD_ACTIVITY_LOG"] });
      toast.success("Tags updated");
    },
    onError: (error, change) => {
      // Undo only this change, so a concurrent change on the same Lead survives.
      if (change.kind === "add")
        updateLeadTagsInCaches(queryClient, leadId, (tags) =>
          withoutTag(tags, change.tag)
        );
      if (change.kind === "remove")
        updateLeadTagsInCaches(queryClient, leadId, (tags) =>
          withTag(tags, change.tag)
        );
      toast.error(getErrorMessage(error, "Failed to update tags"));
    }
  });
};
